import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import Header from '../components/layout/Header';
import {
  FiArrowLeft,
  FiDollarSign,
  FiCheckCircle,
  FiAlertCircle,
  FiCalendar,
  FiCreditCard,
  FiDownload,
  FiInfo
} from 'react-icons/fi';

interface BillCharge {
  id: string;
  description: string;
  amount: number;
  type: 'tuition' | 'fees' | 'housing' | 'meal_plan' | 'other';
  dueDate: string;
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  method: string;
  status: 'completed' | 'pending' | 'failed';
  description: string;
  confirmationNumber: string;
}

interface BillingSummary {
  semester: string;
  totalCharges: number;
  totalPayments: number;
  balanceDue: number;
  dueDate: string;
  charges: BillCharge[];
  payments: Payment[];
}

const MyBillPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSemester, setSelectedSemester] = useState('Fall 2025');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBillingData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch billing charges
        const chargesQuery = query(
          collection(db, 'billingCharges'),
          where('userId', '==', currentUser.uid),
          where('semester', '==', selectedSemester)
        );

        const chargesSnapshot = await getDocs(chargesQuery);
        const charges = chargesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as BillCharge));

        // Fetch payment history
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('userId', '==', currentUser.uid),
          where('semester', '==', selectedSemester),
          orderBy('date', 'desc')
        );

        const paymentsSnapshot = await getDocs(paymentsQuery);
        const payments = paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Payment));

        const totalCharges = charges.reduce((sum, charge) => sum + charge.amount, 0);
        const totalPayments = payments
          .filter(p => p.status === 'completed')
          .reduce((sum, payment) => sum + payment.amount, 0);

        setBillingSummary({
          semester: selectedSemester,
          totalCharges,
          totalPayments,
          balanceDue: totalCharges - totalPayments,
          dueDate: charges[0]?.dueDate || 'TBD',
          charges,
          payments
        });

      } catch (err) {
        console.error('Error fetching billing data:', err);
        setError('Failed to load billing information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, [currentUser, selectedSemester]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: {
        icon: <FiCheckCircle size={14} />,
        text: 'Completed',
        color: 'bg-green-100 text-green-800'
      },
      pending: {
        icon: <FiAlertCircle size={14} />,
        text: 'Pending',
        color: 'bg-yellow-100 text-yellow-800'
      },
      failed: {
        icon: <FiAlertCircle size={14} />,
        text: 'Failed',
        color: 'bg-red-100 text-red-800'
      }
    };

    const badge = badges[status as keyof typeof badges];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.icon}
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-uc-navy">Loading billing information...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">MY BILL</h1>
          <p className="text-gray-600 mt-2">View your student account balance and payment history</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiAlertCircle className="text-red-600 mt-0.5" size={20} />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Semester Selector */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <FiCalendar className="text-uc-gold" size={24} />
              <h2 className="text-xl font-bold text-uc-navy">Academic Term</h2>
            </div>
            <select
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
            >
              <option value="Fall 2025">Fall 2025</option>
              <option value="Spring 2025">Spring 2025</option>
              <option value="Fall 2024">Fall 2024</option>
            </select>
          </div>
        </div>

        {/* Account Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Total Charges</p>
                <p className="text-3xl font-bold text-uc-navy mt-2">
                  {formatCurrency(billingSummary?.totalCharges || 0)}
                </p>
              </div>
              <FiDollarSign className="text-uc-gold" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Total Payments</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {formatCurrency(billingSummary?.totalPayments || 0)}
                </p>
              </div>
              <FiCheckCircle className="text-green-500" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Balance Due</p>
                <p className={`text-3xl font-bold mt-2 ${
                  (billingSummary?.balanceDue || 0) > 0 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {formatCurrency(billingSummary?.balanceDue || 0)}
                </p>
              </div>
              <FiAlertCircle className={
                (billingSummary?.balanceDue || 0) > 0 ? 'text-red-500' : 'text-green-500'
              } size={40} />
            </div>
          </div>
        </div>

        {/* Balance Due Alert */}
        {billingSummary && billingSummary.balanceDue > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <FiAlertCircle className="text-yellow-600 mt-1" size={24} />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-yellow-900 mb-2">Payment Due</h3>
                <p className="text-yellow-800 mb-3">
                  You have an outstanding balance of <strong>{formatCurrency(billingSummary.balanceDue)}</strong>.
                  Payment is due by {formatDate(billingSummary.dueDate)}.
                </p>
                <button className="bg-uc-gold text-white px-6 py-2 rounded-lg hover:bg-yellow-500 transition-colors font-medium">
                  Make a Payment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Charges Breakdown */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-uc-navy">Charges</h2>
              <p className="text-gray-600 text-sm mt-1">{selectedSemester}</p>
            </div>
            <button className="flex items-center gap-2 text-uc-navy hover:text-uc-gold transition-colors">
              <FiDownload size={18} />
              <span className="text-sm font-medium">Download Statement</span>
            </button>
          </div>

          {billingSummary && billingSummary.charges.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {billingSummary.charges.map((charge) => (
                    <tr key={charge.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-medium text-uc-navy">{charge.description}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 capitalize">
                          {charge.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-900 text-sm">
                        {formatDate(charge.dueDate)}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-uc-navy">
                        {formatCurrency(charge.amount)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-bold">
                    <td colSpan={3} className="px-6 py-4 text-right text-uc-navy">
                      Total Charges:
                    </td>
                    <td className="px-6 py-4 text-right text-uc-navy">
                      {formatCurrency(billingSummary.totalCharges)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <FiDollarSign size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No charges for {selectedSemester}</p>
            </div>
          )}
        </div>

        {/* Payment History */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-uc-navy">Payment History</h2>
            <p className="text-gray-600 text-sm mt-1">
              Monthly payments for {selectedSemester}
            </p>
          </div>

          {billingSummary && billingSummary.payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Confirmation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {billingSummary.payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-gray-900 text-sm whitespace-nowrap">
                        {formatDate(payment.date)}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-900">{payment.description}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FiCreditCard className="text-gray-400" size={16} />
                          <span className="text-gray-900 text-sm">{payment.method}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 font-mono text-xs">
                        {payment.confirmationNumber}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-green-600">
                        {formatCurrency(payment.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <FiCreditCard size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No payment history for {selectedSemester}</p>
            </div>
          )}
        </div>

        {/* Payment Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
            <FiInfo size={20} />
            Payment Information
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>
                <strong>Monthly Payment Plan:</strong> $21,000 annual tuition divided into monthly installments
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Payments are due on the 1st of each month</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Late payments may result in holds on your account and additional fees</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Payment methods accepted: Credit/Debit Card, ACH Bank Transfer, Check, or Money Order</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>For questions about your bill, contact Student Accounts at (209) 228-4444</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default MyBillPage;
